import { Input, mergeClasses, Tooltip } from "@fluentui/react-components";
import { StarFilled } from "@fluentui/react-icons";
import { moveConversation, updateConversationTitle } from "api";
import { ConstantValues } from "common/constants";
import { CopyOptionsDialog } from "components/conversations/copyOptionsDialog/CopyOptionsDialog";
import { MoveConversationDialog } from "components/conversations/moveConversationDialog/MoveConversationDialog";
import { IConversation } from "models/IConversation";
import { IWorkspace } from "models/IWorkspace";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "hooks/useIsMobile";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { getRelativeTime } from "utils/generalUtils";
import { ChatListItemMenu } from "../chatListItemMenu/ChatListItemMenu";
import { useStyles } from "./ChatListItem.styles";
import { IChatListItemProps } from "./ChatListItem.types";

const ChatListItemComponent = ({
  conversation,
  currentUser,
  isSelected,
  isLoading = false,
  searchText,
  workspaceName,
  onSelect,
  onFavorite,
  onDelete,
  onRename,
  onMove,
  onCopy,
}: IChatListItemProps): JSX.Element => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [newTitle, setNewTitle] = useState<string>(conversation.title);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState<boolean>(false);
  const [isCopyOptionsDialogOpen, setIsCopyOptionsDialogOpen] =
    useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const workspaces: IWorkspace[] = useSelector(
    (state: RootState) => state.workspacesSlice.value,
  );
  const personalWorkspaceId: string | undefined = workspaces.find(
    (ws: IWorkspace) => ws.name === ConstantValues.PERSONAL_WORKSPACE,
  )?.id;

  const isCurrentUserOwner: boolean = conversation.userId === currentUser?.id;

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  useEffect(() => {
    setNewTitle(conversation.title);
  }, [conversation.title]);

  const handleClick = () => {
    if (isRenaming) return;
    onSelect(conversation);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isRenaming) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(conversation);
    }
  };

  const handleFavorite = () => {
    onFavorite(conversation);
  };

  const handleDelete = () => {
    onDelete(conversation);
  };

  const handleMoveConversation = async (
    newWorkspaceId: string,
  ): Promise<void> => {
    const updatedConversation: IConversation = {
      ...conversation,
      workspaceId: newWorkspaceId,
      updatedAt: Math.floor(Date.now() / 1000),
    };
    await moveConversation({
      conversationId: conversation.id,
      workspaceId: newWorkspaceId,
    });
    onMove(updatedConversation, newWorkspaceId);
    setIsMoveDialogOpen(false);
  };

  const handleRenameConfirm = () => {
    const trimmed = newTitle.trim();
    setIsRenaming(false);
    setTimeout(() => containerRef.current?.focus(), 0);

    if (trimmed && trimmed !== conversation.title.trim()) {
      setNewTitle(trimmed);
      onRename(conversation, trimmed);
      updateConversationTitle({ conversation, title: trimmed }).catch((e) => {
        console.error("Failed to rename conversation:", e);
        setNewTitle(conversation.title);
      });
    } else {
      setNewTitle(conversation.title);
    }
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRenameConfirm();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsRenaming(false);
      setNewTitle(conversation.title);
      setTimeout(() => containerRef.current?.focus(), 0);
    }
  };

  const lastUpdatedTime = getRelativeTime(conversation.updatedAt);

  const highlightTitle = useMemo(() => {
    if (!searchText || !searchText.trim()) {
      return newTitle;
    }

    if (!newTitle.toLowerCase().includes(searchText.trim().toLowerCase())) {
      return newTitle;
    }

    const escapedSearchText = searchText
      .trim()
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedSearchText})`, "gi");
    const parts = newTitle.split(regex);

    return parts.map((part, index) =>
      index % 2 === 1 ? (
        <span key={index} className={styles.highlightedText}>
          {part}
        </span>
      ) : (
        <span key={index}>{part}</span>
      ),
    );
  }, [newTitle, searchText, styles.highlightedText]);

  const containerClassName = mergeClasses(
    styles.container,
    (isSelected || isMenuOpen) && styles.containerSelected,
    isLoading && styles.containerLoading,
  );

  return (
    <>
      <div
        ref={containerRef}
        className={containerClassName}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="button"
        tabIndex={0}
        aria-label={`Select chat: ${conversation.title}, last updated ${lastUpdatedTime}${personalWorkspaceId && conversation.workspaceId !== personalWorkspaceId ? `${workspaceName ? `, workspace: ${workspaceName}` : ""}, ${conversation.lastUpdatedByDisplayName ? `, last updated by ${conversation.lastUpdatedByDisplayName}` : ""}` : ""}`}
      >
        <div className={styles.contentArea}>
          {isRenaming ? (
            <Input
              ref={inputRef}
              value={newTitle}
              onChange={(_e, data) => setNewTitle(data.value)}
              onKeyDown={handleRenameKeyDown}
              onBlur={handleRenameConfirm}
              onClick={(ev) => ev.stopPropagation()}
              size="small"
              aria-label="Rename chat"
            />
          ) : (
            <div className={styles.title}>
              {conversation.isFavorited && (
                <StarFilled className={styles.favoritedIcon} />
              )}
              <span className={styles.titleText}>{highlightTitle}</span>
            </div>
          )}

          <div className={styles.metadataRow}>
            {workspaceName &&
              (isMobile ? (
                <Tooltip content={workspaceName} relationship="label">
                  <span
                    className={mergeClasses(
                      styles.metadataItemMobile,
                      styles.workspaceItemAsItalic,
                    )}
                    title={workspaceName}
                  >
                    {workspaceName.slice(0, 30)}
                    {workspaceName?.length > 30 ? "..." : ""}
                  </span>
                </Tooltip>
              ) : (
                <Tooltip content={workspaceName} relationship="label">
                  <span
                    className={mergeClasses(
                      styles.metadataItem,
                      styles.workspaceItemAsItalic,
                    )}
                    title={workspaceName}
                  >
                    {workspaceName.slice(0, 70)}
                    {workspaceName?.length > 70 ? "..." : ""}
                  </span>
                </Tooltip>
              ))}
            <div className={styles.flexRowForWorkspaces}>
              <span className={styles.metadataItem}>
                Last updated {lastUpdatedTime}
              </span>

              {conversation.workspaceId !== personalWorkspaceId && (
                <>
                  {conversation.lastUpdatedByDisplayName && (
                    <span
                      className={styles.metadataItem}
                      title={conversation.lastUpdatedByDisplayName}
                    >
                      by {conversation.lastUpdatedByDisplayName}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div
          data-testid="chat-list-item-menu-area"
          className={mergeClasses(
            styles.menuArea,
            (isHovered || isSelected || isMenuOpen) && styles.menuAreaVisible,
          )}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ChatListItemMenu
            conversation={conversation}
            personalWorkspaceId={personalWorkspaceId}
            isCurrentUserOwner={isCurrentUserOwner}
            setIsRenaming={setIsRenaming}
            setIsMoveDialogOpen={setIsMoveDialogOpen}
            setIsCopyOptionsDialogOpen={setIsCopyOptionsDialogOpen}
            onFavorite={handleFavorite}
            onDelete={handleDelete}
            onOpenChange={setIsMenuOpen}
          />
        </div>
      </div>

      {isMoveDialogOpen && (
        <MoveConversationDialog
          isOpen={isMoveDialogOpen}
          onClose={() => setIsMoveDialogOpen(false)}
          onMove={handleMoveConversation}
        />
      )}

      {isCopyOptionsDialogOpen && (
        <CopyOptionsDialog
          conversation={conversation}
          isOpen={isCopyOptionsDialogOpen}
          onClose={() => setIsCopyOptionsDialogOpen(false)}
          onCopy={async (options) => {
            setIsCopyOptionsDialogOpen(false);
            if (options) {
              return onCopy(conversation, options);
            }
            return [];
          }}
        />
      )}
    </>
  );
};

export const ChatListItem = memo(ChatListItemComponent);
